import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Rocket, Plus, RefreshCw, ChevronDown, Trash2 } from "lucide-react";

// The 8 ships available in the exec hangar
const DEFAULT_SHIPS = [
  "Anvil F7A Hornet Mk II Executive",
  "Anvil F8C Lightning Executive",
  "Drake Corsair Executive",
  "Drake Cutlass Black Executive",
  "Gatac Syulen Executive",
  "Mirai Guardian MX Wikelo War Special",
  "Robert Space Industries Meteor Executive",
  "Robert Space Industries Scorpius Wikelo War Special",
];

function generateCycleId() {
  // Use the current UTC date + hour-block as a stable cycle identifier
  const now = new Date();
  const y = now.getUTCFullYear();
  const mo = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  const h = String(now.getUTCHours()).padStart(2, "0");
  return `cycle_${y}${mo}${d}_${h}`;
}

// Minimal styled select that matches the dashboard aesthetic
function StyledSelect({ value, onChange, placeholder, options, disabled }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full appearance-none px-3 py-2 pr-8 bg-[#0B0E14] border border-slate-700 text-white text-sm focus:outline-none focus:border-orange-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
    </div>
  );
}

export default function ShipClaimsDialog({ isAdmin, mode }) {
  const isPrivate = mode === "private";
  const accentText = isPrivate ? "text-emerald-400" : "text-orange-400";
  const topClaimerColor = "text-emerald-400";
  const [ships, setShips] = useState([]);
  const [nicknames, setNicknames] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [userShips, setUserShips] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Claim form state
  const [selectedNickname, setSelectedNickname] = useState("");
  const [selectedShip, setSelectedShip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [nicknamesError, setNicknamesError] = useState(null);

  // Admin: add ship
  const [newShipName, setNewShipName] = useState("");
  const [addingShip, setAddingShip] = useState(false);
  const [addShipError, setAddShipError] = useState(null);

  // Admin: reset analytics
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadShips = useCallback(async () => {
    try {
      const { data } = await api.get("/ships");
      setShips(data);
    } catch {
      // If no ships in DB yet, fall back to the default list displayed as read-only
      setShips([]);
    }
  }, []);

  const loadNicknames = useCallback(async () => {
    setNicknamesError(null);
    try {
      const { data } = await api.get("/users/whitelist-nicknames");
      setNicknames(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const message =
        status === 401 || status === 403
          ? "Session expired — please refresh the page and log in again."
          : typeof detail === "string"
          ? detail
          : "Failed to load user list. Check your connection and try refreshing.";
      console.error("[ShipClaimsDialog] loadNicknames failed:", e);
      setNicknamesError(message);
      setNicknames([]);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const [distRes, userRes] = await Promise.all([
        api.get("/metrics/ship-distribution"),
        api.get("/metrics/user-ships"),
      ]);
      setDistribution(distRes.data);
      setUserShips(userRes.data);
    } catch {
      // silently ignore
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShips();
    loadNicknames();
    loadMetrics();
  }, [loadShips, loadNicknames, loadMetrics]);

  // Derive the ship options: merge default ships with any custom DB ships,
  // deduplicate by name, and sort alphabetically
  const shipOptions = Array.from(
    new Map(
      [
        ...DEFAULT_SHIPS.map((s) => ({ value: s, label: s })),
        ...ships.map((s) => ({ value: s.name, label: s.name })),
      ].map((opt) => [opt.label, opt])
    ).values()
  ).sort((a, b) => a.label.localeCompare(b.label));

  const nicknameOptions = nicknames.map((n) => ({
    value: n.nickname,
    label: n.nickname,
  }));

  const handleSubmitClaim = async () => {
    if (!selectedNickname || !selectedShip) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      await api.post("/ship-claims", {
        user_nickname: selectedNickname,
        ship_name: selectedShip,
        hangar_cycle_id: generateCycleId(),
      });
      setSubmitSuccess(true);
      setSelectedNickname("");
      setSelectedShip("");
      // Refresh metrics after a successful claim
      loadMetrics();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setSubmitError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? String(d)).join("; ")
          : "Failed to record claim"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddShip = async () => {
    const name = newShipName.trim();
    if (!name) return;
    setAddingShip(true);
    setAddShipError(null);
    try {
      await api.post("/ships", { name });
      setNewShipName("");
      loadShips();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setAddShipError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d?.msg ?? String(d)).join("; ")
          : "Failed to add ship"
      );
    } finally {
      setAddingShip(false);
    }
  };

  const handleDeleteShip = async (shipId) => {
    try {
      await api.delete(`/ships/${shipId}`);
      loadShips();
    } catch {
      // silently ignore
    }
  };

  const handleResetAnalytics = async () => {
    setResetting(true);
    try {
      await api.delete("/ship-claims");
      setResetConfirmOpen(false);
      loadMetrics();
    } catch {
      // silently ignore
    } finally {
      setResetting(false);
    }
  };

  const maxCount =
    distribution.length > 0
      ? Math.max(...distribution.map((d) => d.claim_count))
      : 1;

  // Determine the top claimers for the leaderboard highlight.
  // ALL users tied for the highest total_claims get cyan.
  // Among those tied users, the one with the most recent last_claimed_at
  // also gets bold + larger font.
  const { topClaimers, topMostRecent } = (() => {
    if (userShips.length === 0) return { topClaimers: new Set(), topMostRecent: null };
    const maxClaims = Math.max(...userShips.map((u) => u.total_claims ?? 0));
    if (maxClaims === 0) return { topClaimers: new Set(), topMostRecent: null };
    const tied = userShips.filter((u) => (u.total_claims ?? 0) === maxClaims);
    const topClaimers = new Set(tied.map((u) => u.user_nickname));
    // Among tied users, find the one with the most recent claim timestamp
    const mostRecent = tied.reduce((best, u) => {
      const bestTs = best.last_claimed_at ? new Date(best.last_claimed_at).getTime() : 0;
      const uTs = u.last_claimed_at ? new Date(u.last_claimed_at).getTime() : 0;
      return uTs > bestTs ? u : best;
    });
    return { topClaimers, topMostRecent: mostRecent.user_nickname };
  })();

  // Sort users: primary by total_claims descending, secondary by last_claimed_at descending
  const sortedUserShips = [...userShips].sort((a, b) => {
    const claimDiff = (b.total_claims ?? 0) - (a.total_claims ?? 0);
    if (claimDiff !== 0) return claimDiff;
    const aTs = a.last_claimed_at ? new Date(a.last_claimed_at).getTime() : 0;
    const bTs = b.last_claimed_at ? new Date(b.last_claimed_at).getTime() : 0;
    return bTs - aTs;
  });

  return (
    <div data-testid="ship-claims-panel">
      {/* ── Claim form ─────────────────────────────────────────────── */}
      <div className="border border-slate-800 bg-[#121822]/60 p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Rocket className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
            Record Ship Claim
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
              User
            </label>
            <StyledSelect
              value={selectedNickname}
              onChange={setSelectedNickname}
              placeholder="Select user…"
              options={nicknameOptions}
              disabled={submitting || !!nicknamesError}
            />
            {nicknamesError ? (
              <p className="text-[10px] text-orange-400 mt-1">
                ⚠ {nicknamesError}
              </p>
            ) : nicknames.length === 0 ? (
              <p className="text-[10px] text-slate-500 mt-1">
                No whitelisted users found. Add users via the admin whitelist.
              </p>
            ) : null}
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1.5">
              Ship Claimed
            </label>
            <StyledSelect
              value={selectedShip}
              onChange={setSelectedShip}
              placeholder="Select ship…"
              options={shipOptions}
              disabled={submitting}
            />
          </div>
        </div>

        {submitError && (
          <div className="px-3 py-2 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-3">
            {submitError}
          </div>
        )}
        {submitSuccess && (
          <div className="px-3 py-2 border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs tracking-wide mb-3">
            Claim recorded successfully.
          </div>
        )}

        <button
          onClick={handleSubmitClaim}
          disabled={submitting || !selectedNickname || !selectedShip}
          data-testid="ship-claim-submit"
          className="flex items-center gap-2 px-5 py-2 border border-orange-500 bg-orange-500/10 text-orange-300 hover:bg-orange-500 hover:text-black transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5" />
          {submitting ? "Saving…" : "Record Claim"}
        </button>
      </div>

      {/* ── Metrics ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] tracking-[0.3em] uppercase text-slate-500">
          Claim Analytics
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={() => setResetConfirmOpen(true)}
              disabled={metricsLoading || resetting}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-orange-500/40 text-[11px] tracking-widest uppercase text-orange-400 hover:border-orange-500 hover:bg-orange-500/10 transition-colors disabled:opacity-40"
            >
              <Trash2 className="w-3 h-3" />
              Reset Analytics
            </button>
          )}
          <button
            onClick={loadMetrics}
            disabled={metricsLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-3 h-3 ${metricsLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Reset Analytics confirmation ────────────────────────────── */}
      {resetConfirmOpen && (
        <div className="border border-orange-500/30 bg-orange-500/5 p-4 mb-4">
          <div className="text-[11px] tracking-[0.3em] uppercase text-orange-400 mb-1">
            Confirm Reset
          </div>
          <div className="text-slate-300 text-sm mb-4">
            This will permanently delete all ship claim records. This action cannot be undone.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setResetConfirmOpen(false)}
              disabled={resetting}
              className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-[11px] tracking-widest uppercase disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleResetAnalytics}
              disabled={resetting}
              className="px-4 py-2 border border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
            >
              {resetting ? "Resetting…" : "Delete All Claims"}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Ship distribution */}
        <div className="border border-slate-800 bg-[#121822]/60 p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-4">
            Ship Distribution
          </div>
          {distribution.length === 0 ? (
            <div className="border border-dashed border-slate-700 p-8 text-center text-slate-600 text-xs">
              No claims recorded yet.
            </div>
          ) : (
            <div className="space-y-3" data-testid="ship-distribution-list">
              {distribution.map((item) => (
                <div key={item.ship_name} data-testid="ship-distribution-row">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-slate-300 text-xs truncate pr-2 max-w-[70%]">
                      {item.ship_name}
                    </span>
                    <span className="text-slate-400 text-xs font-mono whitespace-nowrap">
                      {item.claim_count} ({item.percentage}%)
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full transition-all duration-500"
                      style={{
                        width: `${(item.claim_count / maxCount) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User ships breakdown */}
        <div className="border border-slate-800 bg-[#121822]/60 p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-4">
            Claims by User
          </div>
          {userShips.length === 0 ? (
            <div className="border border-dashed border-slate-700 p-8 text-center text-slate-600 text-xs">
              No claims recorded yet.
            </div>
          ) : (
            <div className="space-y-4 max-h-80 overflow-y-auto" data-testid="user-ships-list">
              {sortedUserShips.map((u) => {
                const isTop = topClaimers.has(u.user_nickname);
                const isTopMostRecent = u.user_nickname === topMostRecent;
                return (
                <div key={u.user_nickname} data-testid="user-ships-row">
                  <div
                    className={
                      isTopMostRecent
                        ? `text-base tracking-[0.2em] uppercase ${topClaimerColor} mb-1.5 font-bold`
                        : isTop
                        ? `text-[11px] tracking-[0.2em] uppercase ${topClaimerColor} mb-1.5 font-medium`
                        : "text-[11px] tracking-[0.2em] uppercase text-orange-400 mb-1.5 font-medium"
                    }
                  >
                    {u.user_nickname}
                  </div>
                  <div className="space-y-1 pl-2">
                    {u.ships_claimed.map((s) => (
                      <div
                        key={s.ship_name}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="text-slate-400 text-xs truncate">
                          {s.ship_name}
                        </span>
                        <span className="text-slate-500 text-xs font-mono whitespace-nowrap">
                          ×{s.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      </div>

      {/* ── Admin: Manage Ships ─────────────────────────────────────── */}
      {isAdmin && (
        <div className="border border-slate-800 bg-[#121822]/60 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Rocket className="w-3.5 h-3.5 text-orange-400" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
              Manage Ships (Admin)
            </span>
          </div>

          {/* Add ship */}
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              placeholder="Ship name…"
              value={newShipName}
              onChange={(e) => setNewShipName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddShip()}
              data-testid="add-ship-input"
              className="flex-1 px-3 py-2 bg-[#0B0E14] border border-slate-700 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-orange-500 transition-colors"
            />
            <button
              onClick={handleAddShip}
              disabled={addingShip || !newShipName.trim()}
              data-testid="add-ship-btn"
              className="px-4 py-2 border border-slate-600 bg-slate-800/60 text-slate-300 hover:border-orange-500 hover:text-orange-300 transition-colors text-[11px] tracking-widest uppercase disabled:opacity-40"
            >
              {addingShip ? "Adding…" : "Add"}
            </button>
          </div>

          {addShipError && (
            <div className="px-3 py-2 border border-orange-500/30 bg-orange-500/10 text-orange-400 text-xs tracking-wide mb-3">
              {addShipError}
            </div>
          )}


          {/* Ship list */}
          {ships.length === 0 ? (
            <div
              className="border border-dashed border-slate-700 px-4 py-3 text-slate-600 text-xs"
              data-testid="ships-empty"
            >
              No ships in database — using default list for the claim form.
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="ships-list">
              {ships.map((ship) => (
                <li
                  key={ship.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 border border-slate-700 bg-[#0B0E14]/60"
                  data-testid="ship-entry"
                >
                  <span className="text-slate-300 text-xs truncate">{ship.name}</span>
                  <button
                    onClick={() => handleDeleteShip(ship.id)}
                    data-testid="ship-delete"
                    aria-label={`Remove ${ship.name}`}
                    className="flex-shrink-0 text-slate-600 hover:text-orange-400 transition-colors text-[10px] tracking-widest uppercase"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
